import Box from '@mui/material/Box';
import React from 'react';

import { FormFieldInput } from '@/components/molecules/FormFieldInput';
import { Field, FieldType } from '@/types/fields';
import { Identified } from '@/types/identified';
import { toEmptyStringIfNull } from '@/utility/toEmptyStringIfNull';

interface Props<T> {
  readonly item?: Identified<T>;
  readonly field: Field<T>;
}

export function ListFormFieldField<T>(props: Props<T>): React.ReactElement {
  const { field: fieldTemplate, item = { id: 'error-error' } } = props;

  const field = React.useMemo((): Field<T> => {
    if (fieldTemplate.type === FieldType.file) {
      const { path } = fieldTemplate;
      return { ...fieldTemplate, path: path.replace('{id}', item.id) };
    } else {
      return fieldTemplate;
    }
  }, [fieldTemplate, item.id]);

  const span = React.useMemo((): number => field.columnSpan ?? 1, [field.columnSpan]);
  const gridColumn = React.useMemo((): string => `auto / span ${span}`, [span]);

  const key = React.useMemo((): string => String(field.name), [field.name]);

  const valueKey = React.useMemo(
    (): keyof typeof item => field.name as keyof typeof item,
    [field.name],
  );

  const value = React.useMemo(
    (): unknown => toEmptyStringIfNull(field.type !== FieldType.embedded ? item[valueKey] : item),
    [field.type, item, valueKey],
  );

  return (
    <Box key={key} gridColumn={gridColumn} minWidth={0}>
      <FormFieldInput field={field} defaultValue={value} />
    </Box>
  );
}
